import { Frame as FrameBase } from '@studiometa/ui';
import { nextTick } from '@studiometa/js-toolkit/utils';

/**
 * Frame component with dynamic mode control
 */
export default class Frame extends FrameBase {
  /**
   * Component Configuration
   */
  static config = {
    ...FrameBase.config,
    name: 'Frame',
    refs: ['loadmore'],
  };

  /**
   * Get frame-list target
   * @returns {object|undefined}
   */
  get frameListTarget() {
    return this.getDirectChildren('FrameTarget').find((target) => target.id === 'frame-list');
  }

  /**
   * Get grid container
   * @returns {HTMLElement|null}
   */
  get gridContainer() {
    const frameListTarget = this.frameListTarget;
    if (!frameListTarget) return null;
    return (
      frameListTarget.$el.querySelector('.grid') ||
      frameListTarget.$el.querySelector('[data-component="ViewList"]')
    );
  }

  /**
   * Intercept FrameAnchor trigger to handle load-more with manual animation
   * @param {object} props
   */
  onFrameAnchorFrameTrigger(props) {
    const [url, requestInit] = props.args;
    const trigger = requestInit.trigger;
    const isLoadMore = trigger.$el.classList.contains('frame-load-more');

    if (isLoadMore) {
      this.handleLoadMore(url, trigger.$el);
      return;
    }

    super.onFrameAnchorFrameTrigger({ args: [url, requestInit] });
  }

  /**
   * Handle load-more with animations
   * @param {string} url
   * @param {HTMLElement} triggerEl
   * @returns {void}
   */
  handleLoadMore(url, triggerEl) {
    triggerEl.classList.add('is-loading');

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newGridContainer = html.querySelector('.grid');
        const newFrameLoadmore = html.querySelector('[id="frame-loadmore"]');

        if (!newGridContainer) return;

        const newArticles = Array.from(newGridContainer.children);

        this.hideNewArticles(newArticles);

        nextTick(() => {
          const grid = this.gridContainer;
          newArticles.forEach((article) => {
            grid?.append(article);
          });
        });

        if (newFrameLoadmore) {
          const frameLoadmoreTarget = this.getDirectChildren('FrameTarget').find(
            (target) => target.id === 'frame-loadmore',
          );
          if (frameLoadmoreTarget) {
            frameLoadmoreTarget.$el.innerHTML = newFrameLoadmore.innerHTML;
          }
        }

        this.$update();

        nextTick(() => {
          setTimeout(() => {
            this.showNewArticles();
          }, 200);
        });
      })
      .catch(() => {
        triggerEl.classList.remove('is-loading');
      });
  }

  /**
   * Hide new articles before insertion
   * @param {Array} articles
   * @returns {void}
   */
  hideNewArticles(articles) {
    articles.forEach((article) => {
      article.classList.add(
        'transition',
        'duration-700',
        'ease-out-quad',
        'translate-y-4',
        'opacity-0',
      );
    });
  }

  /**
   * Show articles with animation
   * @returns {void}
   */
  showNewArticles() {
    const grid = this.gridContainer;
    if (!grid) return;

    const newArticles = Array.from(grid.children).filter((el) => {
      return el.classList.contains('translate-y-4');
    });

    newArticles.forEach((article) => {
      article.classList.remove('translate-y-4', 'opacity-0');
    });
  }
}
